import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Overlay } from "@/components/sections/Overlay";
import { SectionButton } from "@/components/sections/SectionButton";
import { HeroMarquee } from "@/components/layout/HeroMarquee";
import { RichText } from "@/components/common/RichText";
import { BgVideo } from "@/components/common/BgVideo";
import { mediaUrl } from "@/lib/usePage";
import * as Icons from "lucide-react";

const focal = (m) => ({ objectPosition: `${m?.focal_x ?? 50}% ${m?.focal_y ?? 50}%` });

// Background media (video preferred, else image) used by hero / cta sections.
const MediaBg = ({ media = {} }) => {
  if (media.video_url) {
    return (
      <video
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay muted loop playsInline preload="metadata"
        poster={media.image_url ? mediaUrl(media.image_url) : undefined}
        style={focal(media)}
        aria-hidden="true"
      >
        <source src={mediaUrl(media.video_url)} />
      </video>
    );
  }
  if (media.image_url) {
    return <img src={mediaUrl(media.image_url)} alt={media.alt || ""} loading="lazy" style={focal(media)} className="absolute inset-0 h-full w-full object-cover" />;
  }
  return null;
};

// Rotating background slides (crossfade) for the hero.
const HeroSlides = ({ slides = [], focalStyle }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setI((p) => (p + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);
  return (
    <div className="absolute inset-0" aria-hidden="true">
      {slides.map((src, idx) => (
        <img
          key={src}
          src={mediaUrl(src)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms]"
          style={{ ...focalStyle, opacity: idx === i ? 1 : 0 }}
        />
      ))}
    </div>
  );
};

// HERO — fully editable header: foreground logo, two-tone title, supporting line,
// subtitle, buttons, over a background image / video / rotating slides + overlay.
export const SectionHero = ({ section }) => {
  const { content = {}, media = {}, overlay = {}, buttons = [] } = section;
  const slides = Array.isArray(media.slides) ? media.slides.filter(Boolean) : [];
  const colors = content.colors || {};
  // Only apply an inline color when the editor has explicitly chosen one (truthy).
  const colorStyle = (key) => (colors[key] ? { color: colors[key] } : undefined);
  return (
    <div className="bg-background">
      <section className="section-angle-bottom relative overflow-hidden bg-black text-white grain">
        {slides.length ? <HeroSlides slides={slides} focalStyle={focal(media)} /> : <MediaBg media={media} />}
        <Overlay overlay={overlay && overlay.type ? overlay : { type: "black", opacity: 50 }} />
        <div className="relative z-[2] mx-auto w-full max-w-6xl px-4 sm:px-6 pt-16 pb-24 sm:pt-24 sm:pb-28">
          {content.logo_url && (
            <img src={mediaUrl(content.logo_url)} alt={`${content.heading_left || ""}${content.heading_right || ""}`.trim() || "Logo"} className="mb-6 h-36 w-auto sm:h-52 lg:h-60" data-testid="hero-logo" />
          )}
          {content.eyebrow && <div className="font-condensed text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-brand mb-3">{content.eyebrow}</div>}
          {(content.heading_left || content.heading_right) && (
            <h1 className="text-5xl sm:text-6xl lg:text-7xl">
              {content.heading_right ? (
                <span className="inline-flex flex-wrap items-baseline gap-x-3"><span className="title-impact" style={colorStyle("heading_left")} data-testid="hero-title-left">{content.heading_left}</span><span className="title-futura" style={colorStyle("heading_right")} data-testid="hero-title-right">{content.heading_right}</span></span>
              ) : (<span className="title-impact" style={colorStyle("heading_left")} data-testid="hero-title-left">{content.heading_left}</span>)}
            </h1>
          )}
          {content.supporting_line && <p className="mt-3 font-condensed text-lg sm:text-2xl font-semibold uppercase tracking-[0.25em] text-brand" style={colorStyle("supporting_line")} data-testid="hero-supporting-line">{content.supporting_line}</p>}
          {content.subhead && <RichText html={content.subhead} className="mt-4 max-w-2xl text-base sm:text-lg font-medium text-white leading-relaxed" style={colorStyle("subhead")} testid="hero-subhead" />}
          {buttons.length > 0 && <div className="mt-6 flex flex-wrap gap-3">{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
        </div>
      </section>
      <HeroMarquee />
    </div>
  );
};

// RICH TEXT — heading + body copy (+ optional eyebrow, subtitle, alignment)
export const SectionRichText = ({ section }) => {
  const { content = {}, buttons = [] } = section;
  const center = content.align === "center";
  return (
    <section className="bg-background py-14 sm:py-20">
      <div className={`mx-auto w-full max-w-4xl px-4 sm:px-6 ${center ? "text-center" : ""}`}>
        {content.eyebrow && <p className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-2">{content.eyebrow}</p>}
        {content.heading && <h2 className="display-title text-3xl sm:text-4xl">{content.heading}</h2>}
        {content.subtitle && <p className="mt-3 text-muted-foreground text-base sm:text-lg leading-relaxed">{content.subtitle}</p>}
        {content.body && <RichText html={content.body} className="mt-4 text-muted-foreground leading-relaxed" testid="richtext-body" />}
        {buttons.length > 0 && <div className={`mt-6 flex flex-wrap gap-3 ${center ? "justify-center" : ""}`}>{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
      </div>
    </section>
  );
};

// IMAGE + TEXT — two column
export const SectionImageText = ({ section }) => {
  const { content = {}, media = {}, buttons = [] } = section;
  const right = content.image_side === "right";
  return (
    <section className="bg-background py-14 sm:py-20">
      <div className={`mx-auto grid w-full max-w-6xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 ${right ? "" : ""}`}>
        <div className={right ? "lg:order-1" : "lg:order-2"}>
          {media.image_url && <img src={mediaUrl(media.image_url)} alt={media.alt || ""} style={focal(media)} className="aspect-[4/3] w-full rounded-xl object-cover" />}
        </div>
        <div className={right ? "lg:order-2" : "lg:order-1"}>
          {content.eyebrow && <p className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-2">{content.eyebrow}</p>}
          {content.heading && <h2 className="display-title text-3xl sm:text-4xl">{content.heading}</h2>}
          {content.body && <RichText html={content.body} className="mt-4 text-muted-foreground leading-relaxed" testid="imagetext-body" />}
          {buttons.length > 0 && <div className="mt-6 flex flex-wrap gap-3">{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
        </div>
      </div>
    </section>
  );
};

// CTA BAND — heading + buttons. Three looks via content.variant:
//  • "dark" (default): media/overlay background, centered white text
//  • "brand": solid brand-red background, centered white text
//  • "red_image": background image tinted with the brand-red gradient, left-aligned
export const SectionCtaBand = ({ section }) => {
  const { content = {}, media = {}, overlay = {}, buttons = [] } = section;
  const variant = content.variant || "dark";

  if (variant === "brand") {
    return (
      <section className="relative overflow-hidden bg-brand py-16 text-white sm:py-20" style={{ backgroundImage: "linear-gradient(135deg, hsl(var(--brand-red)) 0%, hsl(var(--brand-red-deep)) 100%)" }}>
        <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center gap-5 px-4 sm:px-6 text-center">
          {content.eyebrow && <p className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-white/80">{content.eyebrow}</p>}
          {content.heading && <h2 className="display-title text-4xl sm:text-6xl">{content.heading}</h2>}
          {content.body && <RichText html={content.body} className="max-w-xl text-white/90" testid="ctaband-body" />}
          {buttons.length > 0 && <div className="mt-2 flex flex-wrap justify-center gap-3">{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
        </div>
      </section>
    );
  }

  if (variant === "video_red") {
    const redA = content.red_opacity ?? 42;
    return (
      <section className="bg-background py-14 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <BgVideo
            videoUrl={media.video_url ? mediaUrl(media.video_url) : undefined}
            poster={media.image_url ? mediaUrl(media.image_url) : undefined}
            redOpacity={redA}
            testid="ctaband-video-window"
            className="rounded-2xl"
          >
            <div className="px-6 py-20 text-center sm:px-10 sm:py-28">
              {content.eyebrow && <p className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-white/80 mb-2">{content.eyebrow}</p>}
              {content.heading && <h3 className="display-title text-4xl text-white sm:text-6xl">{content.heading}</h3>}
              {content.body && <RichText html={content.body} className="mx-auto mt-4 max-w-xl text-base text-white/90 sm:text-lg" testid="ctaband-body" />}
              {buttons.length > 0 && <div className="mt-8 flex flex-wrap justify-center gap-3">{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
            </div>
          </BgVideo>
        </div>
      </section>
    );
  }

  if (variant === "red_image") {
    return (
      <section className="relative isolate overflow-hidden bg-black py-20 text-white sm:py-28">
        {media.image_url && <img src={mediaUrl(media.image_url)} alt={media.alt || ""} loading="lazy" style={focal(media)} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(115deg, hsl(var(--brand-red-deep)) 0%, hsl(var(--brand-red) / 0.72) 45%, rgba(0,0,0,0.82) 100%)" }} />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6">
          {content.eyebrow && <div className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-white/80">{content.eyebrow}</div>}
          {content.heading && <h2 className="mt-2 display-title text-4xl sm:text-5xl lg:text-6xl">{content.heading}</h2>}
          {content.body && <RichText html={content.body} className="mt-4 max-w-xl text-lg text-white/90" testid="ctaband-body" />}
          {buttons.length > 0 && <div className="mt-6 flex flex-wrap gap-3">{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
        </div>
      </section>
    );
  }

  return (
    <section className="relative isolate overflow-hidden bg-black py-16 text-white sm:py-24">
      <MediaBg media={media} />
      <Overlay overlay={overlay && overlay.type ? overlay : { type: "black", opacity: 55 }} />
      <div className="relative z-[2] mx-auto w-full max-w-4xl px-4 sm:px-6 text-center">
        {content.heading && <h2 className="display-title text-4xl sm:text-5xl">{content.heading}</h2>}
        {content.body && <RichText html={content.body} className="mx-auto mt-3 max-w-2xl text-white/80" testid="ctaband-body" />}
        {buttons.length > 0 && <div className="mt-6 flex flex-wrap justify-center gap-3">{buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}</div>}
      </div>
    </section>
  );
};

// FEATURE GRID — items[{icon,title,body,image?,link?}]. When items include an
// `image`, cards render as image-topped, linkable "path" cards; otherwise they
// render as the classic icon cards. Section supports eyebrow + subtitle.
export const SectionFeatureGrid = ({ section }) => {
  const { content = {} } = section;
  const items = content.items || [];
  const hasMedia = items.some((it) => it && it.image);
  const ArrowRight = Icons.ArrowRight;

  const CardInner = ({ it }) => {
    const Icon = Icons[it.icon] || Icons.Dumbbell;
    if (it.image) {
      return (
        <div className="group relative h-full overflow-hidden rounded-xl border border-border bg-card transition-colors hover:border-[hsl(var(--brand-red))]">
          <div className="relative h-40 overflow-hidden">
            <img src={mediaUrl(it.image)} alt={it.image_alt || it.title || ""} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-md bg-brand text-white"><Icon className="h-5 w-5" /></span>
            {it.title && <h3 className="absolute bottom-3 left-4 display-title text-2xl text-white">{it.title}</h3>}
          </div>
          <div className="flex items-center justify-between p-5">
            {it.body && <p className="text-sm text-muted-foreground">{it.body}</p>}
            {it.link && <ArrowRight className="ml-3 h-5 w-5 shrink-0 text-brand transition-transform group-hover:translate-x-1" />}
          </div>
        </div>
      );
    }
    return (
      <div className="h-full rounded-xl border border-border bg-card p-6">
        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-brand/15 text-brand"><Icon className="h-5 w-5" /></span>
        <h3 className="mt-4 font-condensed text-lg font-semibold uppercase tracking-wide">{it.title}</h3>
        {it.body && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.body}</p>}
      </div>
    );
  };

  const Item = ({ it, i }) => {
    const key = `${it.title || "feature"}-${i}`;
    if (it.link) {
      const external = /^https?:\/\//i.test(it.link);
      if (external) {
        return <a key={key} href={it.link} target="_blank" rel="noopener noreferrer" data-testid="feature-card-link" className="block"><CardInner it={it} /></a>;
      }
      return <Link key={key} to={it.link} data-testid="feature-card-link" className="block"><CardInner it={it} /></Link>;
    }
    return <div key={key}><CardInner it={it} /></div>;
  };

  return (
    <section className="bg-background py-14 sm:py-20">
      <div className={`mx-auto w-full ${hasMedia ? "max-w-7xl" : "max-w-6xl"} px-4 sm:px-6`}>
        {(content.eyebrow || content.heading || content.subtitle) && (
          <div className="text-center">
            {content.eyebrow && <p className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-2">{content.eyebrow}</p>}
            {content.heading && <h2 className="display-title text-3xl sm:text-4xl">{content.heading}</h2>}
            {content.subtitle && <p className="mx-auto mt-3 max-w-2xl text-muted-foreground leading-relaxed">{content.subtitle}</p>}
          </div>
        )}
        <div className={`${(content.eyebrow || content.heading || content.subtitle) ? "mt-10" : ""} grid gap-6 sm:grid-cols-2 lg:grid-cols-3`}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {items.map((it, i) => <Item key={`${it.title || "feature"}-${i}`} it={it} i={i} />)}
        </div>
      </div>
    </section>
  );
};

// GALLERY — images[]
export const SectionGallery = ({ section }) => {
  const { content = {} } = section;
  const images = content.images || [];
  return (
    <section className="bg-background py-14 sm:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        {content.heading && <h2 className="display-title text-3xl sm:text-4xl">{content.heading}</h2>}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {images.map((img, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <img key={i} src={mediaUrl(img.url || img)} alt={img.alt || ""} className="aspect-square w-full rounded-lg object-cover" />
          ))}
        </div>
      </div>
    </section>
  );
};

// CAROUSEL BANNER — rotating full-width slides with a video/image background and a
// per-slide overlay (red gradient by default, or black with adjustable opacity).
// Auto-rotates (~6s), pauses on hover, with prev/next arrows + dots.
export const SectionCarouselBanner = ({ section }) => {
  const { content = {} } = section;
  const slides = content.slides || [];
  const interval = content.interval || 6000;
  const count = slides.length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), interval);
    return () => clearInterval(t);
  }, [count, paused, interval]);

  useEffect(() => { if (idx >= count && count > 0) setIdx(0); }, [count, idx]);

  if (count === 0) return null;
  const go = (n) => setIdx((n + count) % count);
  const ChevronLeft = Icons.ChevronLeft;
  const ChevronRight = Icons.ChevronRight;

  return (
    <section
      className="relative isolate min-h-[460px] overflow-hidden border-t border-border sm:min-h-[560px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured banners"
      data-testid="carousel-banner"
    >
      {slides.map((slide, i) => {
          const media = slide.media || {};
          const ov = slide.overlay || { type: "red", opacity: 55 };
          const isRed = (ov.type || "red") === "red";
          const active = i === idx;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} aria-hidden={!active} className={`absolute inset-0 transition-opacity duration-700 ${active ? "opacity-100" : "pointer-events-none opacity-0"}`}>
              {media.video_url ? (
                <video autoPlay muted loop playsInline poster={media.image_url ? mediaUrl(media.image_url) : undefined} className={`absolute inset-0 h-full w-full object-cover ${isRed ? "grayscale" : ""}`} style={focal(media)}>
                  <source src={mediaUrl(media.video_url)} />
                </video>
              ) : media.image_url ? (
                <img src={mediaUrl(media.image_url)} alt={media.alt || ""} loading="lazy" className={`absolute inset-0 h-full w-full object-cover ${isRed ? "grayscale" : ""}`} style={focal(media)} />
              ) : <div className="absolute inset-0 bg-black" />}

              {isRed ? (
                <>
                  <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(115deg, hsl(var(--brand-red-deep)) 0%, hsl(var(--brand-red) / 0.72) 45%, rgba(0,0,0,0.82) 100%)" }} />
                  <div className="absolute inset-0 bg-black/25" />
                </>
              ) : (
                <div className="absolute inset-0 bg-black" style={{ opacity: Math.max(0, Math.min(100, ov.opacity ?? 55)) / 100 }} />
              )}

              <div className="relative z-[2] flex min-h-[460px] items-center justify-center px-4 py-20 text-center sm:min-h-[560px] sm:px-6">
                <div className="mx-auto max-w-3xl">
                  {slide.eyebrow && <p className="mb-3 font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-white/80">{slide.eyebrow}</p>}
                  {slide.heading && <h2 className="display-title text-4xl text-white sm:text-5xl lg:text-6xl">{slide.heading}</h2>}
                  {slide.body && <RichText html={slide.body} className="mx-auto mt-4 max-w-xl text-base text-white/90 sm:text-lg" testid={`carousel-slide-body-${i}`} />}
                  {(slide.buttons || []).length > 0 && (
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                      {slide.buttons.map((b) => <SectionButton key={`${b.label || "btn"}-${b.href || ""}`} button={b} />)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {count > 1 && (
          <>
            <button type="button" onClick={() => go(idx - 1)} aria-label="Previous slide" data-testid="carousel-prev" className="absolute left-3 top-1/2 z-[3] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors hover:bg-black/60 sm:left-6"><ChevronLeft className="h-5 w-5" /></button>
            <button type="button" onClick={() => go(idx + 1)} aria-label="Next slide" data-testid="carousel-next" className="absolute right-3 top-1/2 z-[3] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors hover:bg-black/60 sm:right-6"><ChevronRight className="h-5 w-5" /></button>
            <div className="absolute bottom-5 left-1/2 z-[3] flex -translate-x-1/2 items-center gap-2" role="tablist" aria-label="Choose slide">
              {slides.map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <button key={i} type="button" onClick={() => go(i)} role="tab" aria-selected={i === idx} aria-label={`Go to slide ${i + 1}`} data-testid={`carousel-dot-${i}`} className={`h-2.5 rounded-full transition-all duration-200 ${i === idx ? "w-7 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`} />
              ))}
            </div>
          </>
        )}
    </section>
  );
};
